import React, { ReactNode, useCallback, useState } from 'react'

import {
  AddNavigationToQueryInputs,
  AddProjectAdditionToQueryInputs,
  AddSearchQueryInputs,
  ISearchSessionContext,
  SearchSessionContext,
  UpdateSearchQueryResultIdsInputs,
} from './SearchSessionContext'
import useStateWithRef from '../../../hooks/useStateWithRef'
import {
  AddNavigationToSearchQueryInputDto,
  AddProjectAdditionToSearchQueryInputDto,
  AddQueryToSearchSessionInputDto,
  AddQueryToSearchSessionOutputDto,
  SearchSessionSlackInputDto,
  SendSearchSessionSlackNotificationOutputDto,
  UpdateQueryResultIdsInputDto,
} from '../../../lib/graphql'

export interface SearchSessionProviderMethodsProps {
  addQueryToSearchSession: (
    inputs: AddQueryToSearchSessionInputDto,
  ) => Promise<AddQueryToSearchSessionOutputDto>
  addNavigationToSearchQuery: (
    inputs: AddNavigationToSearchQueryInputDto,
  ) => Promise<boolean>
  addProjectAdditionToSearchQuery: (
    inputs: AddProjectAdditionToSearchQueryInputDto,
  ) => Promise<boolean>
  sendSearchSessionSlackNotification?: (
    inputs: SearchSessionSlackInputDto,
  ) => Promise<SendSearchSessionSlackNotificationOutputDto>
  updateSearchQueryResultIds: (
    inputs: UpdateQueryResultIdsInputDto,
  ) => Promise<boolean>
}

interface SearchSessionProviderProps {
  children: ReactNode
  methods: SearchSessionProviderMethodsProps
}

export function SearchSessionProvider({
  children,
  methods,
}: SearchSessionProviderProps) {
  const [_, setSessionId, sessionIdRef] = useStateWithRef<string | undefined>(
    undefined,
  )
  const [searchId, setSearchId] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const addSearchQuery = useCallback(
    async (params: AddSearchQueryInputs): Promise<void> => {
      const { sessionId: _sessionId, searchId: _searchId } =
        await methods.addQueryToSearchSession({
          ...params,
          sessionId: sessionIdRef.current,
        })
      setSessionId(_sessionId)
      setSearchId(_searchId)
    },
    [sessionIdRef.current],
  )

  const addNavigationToQuery = useCallback(
    async (params: AddNavigationToQueryInputs): Promise<void> => {
      if (!sessionIdRef.current || !searchId) {
        throw new Error('SessionId or searchId is missing')
      }
      await methods.addNavigationToSearchQuery({
        ...params,
        sessionId: sessionIdRef.current,
        searchId,
      })
    },
    [sessionIdRef.current, searchId],
  )

  const addProjectAdditionToQuery = useCallback(
    async (params: AddProjectAdditionToQueryInputs): Promise<void> => {
      if (!sessionIdRef.current || !searchId) {
        throw new Error('SessionId or searchId is missing')
      }
      await methods.addProjectAdditionToSearchQuery({
        ...params,
        sessionId: sessionIdRef.current,
        searchId,
      })
    },
    [sessionIdRef.current, searchId],
  )

  const sendSlackNotification = useCallback(
    async (inputs: SearchSessionSlackInputDto) => {
      setIsLoading(true)
      if (methods?.sendSearchSessionSlackNotification) {
        const response = await methods.sendSearchSessionSlackNotification({
          ...inputs,
          sessionId: sessionIdRef.current,
        })
        if (response?.sessionId && !sessionIdRef.current) {
          setSessionId(response.sessionId)
        }
      }
      setIsLoading(false)
    },
    [sessionIdRef.current],
  )

  const updateSearchQueryResultIds = useCallback(
    async (params: UpdateSearchQueryResultIdsInputs) => {
      if (!sessionIdRef.current || !searchId) {
        throw new Error('SessionId or searchId is missing')
      }
      await methods.updateSearchQueryResultIds({
        ...params,
        sessionId: sessionIdRef.current,
        searchId,
      })
    },
    [sessionIdRef.current, searchId],
  )

  const value: ISearchSessionContext = {
    searchId,
    sessionId: sessionIdRef.current ?? undefined,
    addSearchQuery,
    addNavigationToQuery,
    addProjectAdditionToQuery,
    sendSlackNotification,
    isLoading,
    updateSearchQueryResultIds,
  }

  return (
    <SearchSessionContext.Provider value={value}>
      {children}
    </SearchSessionContext.Provider>
  )
}
